import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  objectId,
  type CompanyLocation,
  type Season,
} from "@mysnowscape/snow-lib";

import {
  LockPosition,
  RouteColorName,
  ProposedRoute,
  RouteBuilderProperty,
  RouteBuilderSession,
  RouteGenerationInputs,
  CompanyServiceOption,
  BuilderMode,
  EMPTY_ROUTE_ENDPOINT,
  RouteEndpointLocation,
} from "../../../../../types/routeBuilder";
import type { CompanyLocationFormValues } from "../../../../../components/CompanyLocations/CompanyLocationForm";
import axiosCompanyLocations from "../../../../../services/axios/companyLocations";
import flash from "../../../../../services/flash";
import PaywallProvider, {
  usePaywall,
} from "../../../../../components/SiteMap/contexts/PaywallContext";
import { isUsageExceededError } from "../../../../../services/axios/APIError";
import routeBuilder from "../../../../../services/axios/routeBuilder";
import { defaultInputs, resolveGenerationInputs } from "../lib/defaultInputs";
import {
  FOCUS_ROUTE_THRESHOLD,
  MAX_FOCUSED_ROUTES,
  needsRouteFocus,
} from "../lib/routeFocus";
import { RouteEndpointType, ViewMode } from "../types/routeBuilderTypes";
import { useRouteBuilderResources } from "../hooks/useRouteBuilderResources";
import { useRouteBuilderDrafts } from "../hooks/useRouteBuilderDrafts";
import {
  type PropertyPatch,
  UNASSIGNED_ROUTE_ID,
  useRouteBuilderSession,
} from "../hooks/useRouteBuilderSession";
import { useRouteBuilderGenerate } from "../hooks/useRouteBuilderGenerate";
import { flashRouteBuilderError } from "../lib/flashRouteBuilderError";
import { addRoute as addRouteToSession } from "../lib/sessionOps";
import { createSessionId } from "../lib/helpers";
import { selectGenerationProperties } from "../lib/generationProperties";
import type { AddUnassignedFilters } from "../lib/addUnassignedCatalog";

export type { ViewMode, RouteEndpointType };
export { FOCUS_ROUTE_THRESHOLD, MAX_FOCUSED_ROUTES, needsRouteFocus };
export { UNASSIGNED_ROUTE_ID };

type RouteBuilderContextValue = {
  inputs: RouteGenerationInputs;
  setInputs: React.Dispatch<React.SetStateAction<RouteGenerationInputs>>;
  routeNameOverride: string | null;
  setRouteNameOverride: React.Dispatch<React.SetStateAction<string | null>>;
  session: RouteBuilderSession | null;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  selectedRouteId: string | null;
  setSelectedRouteId: (id: string | null) => void;
  selectedPropertyVisitId: string | null;
  setSelectedPropertyVisitId: (id: string | null) => void;
  listRevealNonce: number;
  selectPropertyFromMap: (routeId: string, propertyVisitId: string) => void;
  mapFocusNonce: number;
  mapFocusPropertyVisitId: string | null;
  focusPropertyOnMap: (propertyVisitId: string) => void;
  dirtyRouteIds: Set<string>;
  hasUnsavedSessionChanges: boolean;
  selectedPropertyCount: number;
  allProperties: RouteBuilderProperty[];
  locations: CompanyLocation[];
  availableServices: CompanyServiceOption[];
  focusedRouteIds: string[] | null;
  needsRouteFocusMode: boolean;
  visibleRoutes: ProposedRoute[];
  mapVisibleRoutes: ProposedRoute[];
  isRouteMapVisible: (routeId: string) => boolean;
  toggleRouteMapVisibility: (routeId: string) => void;
  activeDraftId: string | null;
  isBusy: boolean;
  isSavingLocation: boolean;
  isLoadingResources: boolean;
  isRefreshingProperties: boolean;
  builderMode: BuilderMode;
  optimizationBlocked: boolean;
  optimizationCreditsWarning: string;
  showOptimizationNotice: boolean;
  guardOptimization: () => boolean;
  setFocusedRoutes: (ids: string[] | null) => void;
  clearRouteFocus: () => void;
  generate: (routeNames?: string[]) => Promise<void>;
  createBlankSession: (season: Season) => void;
  loadDraft: (draftId: string) => Promise<boolean>;
  openEditSession: (routeIds: string[]) => Promise<boolean>;
  saveSessionAsDraft: (name: string) => Promise<boolean>;
  saveActiveDraftChanges: () => Promise<boolean>;
  createRoutesFromBuilder: () => Promise<{ createdCount: number } | null>;
  resetBuilder: () => void;
  reoptimizeRoute: (routeId: string) => Promise<void>;
  reoptimizeAll: () => Promise<void>;
  movePropertyVisit: (
    fromRouteId: string,
    toRouteId: string,
    propertyVisitId: string,
    toIndex: number,
  ) => void;
  addPropertyVisit: (
    routeId: string,
    property: RouteBuilderProperty,
    toIndex?: number,
  ) => void;
  addPropertiesToUnassigned: (properties: RouteBuilderProperty[]) => void;
  addUnassignedFilters: AddUnassignedFilters;
  setAddUnassignedFilters: (patch: Partial<AddUnassignedFilters>) => void;
  assignPropertyVisitToRoute: (
    propertyVisitId: string,
    routeId: string,
    patch?: PropertyPatch,
  ) => void;
  removePropertyVisit: (propertyVisitId: string) => void;
  unassignPropertyVisit: (propertyVisitId: string) => void;
  confirmMoveToUnassigned: (details: {
    name: string;
    locked: boolean;
  }) => Promise<boolean>;
  resetMoveToUnassignedConfirm: () => void;
  registerOpenUnassignedPanel: (open: () => void) => () => void;
  addRoute: () => void;
  removeRoute: (routeId: string) => void;
  reorderRoutes: (routeId: string, toIndex: number) => void;
  renameRoute: (routeId: string, name: string) => void;
  setRouteColor: (routeId: string, color: RouteColorName) => void;
  setRouteInstructions: (routeId: string, instructions: string) => void;
  updateProperty: (propertyId: string, patch: PropertyPatch) => void;
  setLock: (propertyVisitId: string, lock: LockPosition) => void;
  setSetupLocation: (
    type: RouteEndpointType,
    location: RouteEndpointLocation | null,
  ) => void;
  setRouteEndpoint: (
    routeId: string,
    type: RouteEndpointType,
    location: RouteEndpointLocation | null,
  ) => void;
  saveCompanyLocation: (
    values: CompanyLocationFormValues,
  ) => Promise<CompanyLocation | null>;
  getProperty: (propertyId: string) => RouteBuilderProperty | undefined;
  getPropertyForVisit: (
    propertyVisitId: string,
  ) => RouteBuilderProperty | undefined;
};

const RouteBuilderContext = createContext<RouteBuilderContextValue | null>(
  null,
);

function RouteBuilderProviderInner({
  children,
  onRoutesCreated,
  onRoutesGenerated,
}: {
  children: React.ReactNode;
  onRoutesCreated?: () => void;
  onRoutesGenerated?: () => void;
}) {
  const [inputs, setInputs] = useState<RouteGenerationInputs>(() =>
    defaultInputs([]),
  );
  // Keep the custom name when the setup form unmounts during route review.
  const [routeNameOverride, setRouteNameOverride] = useState<string | null>(
    null,
  );
  const [isBusy, setIsBusy] = useState(false);
  const [isSavingLocation, setIsSavingLocation] = useState(false);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [backendUsageExceededCompanyId, setBackendUsageExceededCompanyId] =
    useState<string | null>(null);
  const [showOptimizationNotice, setShowOptimizationNotice] = useState(false);
  const queryClient = useQueryClient();
  const {
    companyId,
    locations,
    setLocations,
    allProperties,
    availableServices,
    isLoadingResources,
    isRefreshingProperties,
  } = useRouteBuilderResources(inputs);
  const {
    routeOptimizationCreditsUsed,
    routeOptimizationCreditsWarning,
    isLoading: isLoadingPaywall,
  } = usePaywall();
  const refreshAccountUsage = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [`company/${companyId}/account`],
    });
  }, [companyId, queryClient]);
  const optimizationBlocked =
    (companyId !== "" && backendUsageExceededCompanyId === companyId) ||
    (!isLoadingPaywall && routeOptimizationCreditsUsed);
  const guardOptimization = useCallback(() => {
    if (optimizationBlocked) setShowOptimizationNotice(true);
    return !optimizationBlocked;
  }, [optimizationBlocked]);
  const handleOptimizationError = useCallback(
    (err: unknown, fallbackPrefix: string) => {
      // If one user spends the account's last credit, another user's optimize request
      // can fail with 429 after passing our check. Show the upgrade notice.
      if (isUsageExceededError(err)) {
        setBackendUsageExceededCompanyId(companyId);
        setShowOptimizationNotice(true);
        refreshAccountUsage();
      } else {
        flashRouteBuilderError(err, fallbackPrefix);
      }
    },
    [companyId, refreshAccountUsage],
  );
  const effectiveInputs = useMemo(
    () => resolveGenerationInputs(inputs, locations),
    [inputs, locations],
  );
  const selectedProperties = useMemo(
    () =>
      selectGenerationProperties(
        allProperties,
        effectiveInputs.excludedPropertyIds,
      ),
    [allProperties, effectiveInputs.excludedPropertyIds],
  );

  const sessionApi = useRouteBuilderSession({
    setIsBusy,
    guardOptimization,
    handleOptimizationError,
    refreshAccountUsage,
  });

  const draftsApi = useRouteBuilderDrafts({
    companyId,
    locations,
    session: sessionApi.session,
    setSession: sessionApi.setSession,
    setInputs,
    setSelectedRouteId: sessionApi.setSelectedRouteId,
    setSelectedPropertyId: sessionApi.setSelectedPropertyVisitId,
    setDirtyRouteIds: sessionApi.setDirtyRouteIds,
    setHasUnsavedSessionChanges: sessionApi.setHasUnsavedSessionChanges,
    setFocusedRouteIds: sessionApi.setFocusedRouteIds,
    setIsBusy,
    activeDraftId,
    setActiveDraftId,
  });

  const {
    setSession,
    setSelectedRouteId,
    setSelectedPropertyVisitId,
    setDirtyRouteIds,
    setHasUnsavedSessionChanges,
    setFocusedRouteIds,
    setRouteEndpoint: setSessionRouteEndpoint,
  } = sessionApi;

  const resetBuilder = useCallback(() => {
    setSession(null);
    setInputs(defaultInputs(locations));
    setRouteNameOverride(null);
    setSelectedRouteId(null);
    setSelectedPropertyVisitId(null);
    setDirtyRouteIds(new Set());
    setHasUnsavedSessionChanges(false);
    setFocusedRouteIds(null);
    setActiveDraftId(null);
  }, [
    locations,
    setSession,
    setSelectedRouteId,
    setSelectedPropertyVisitId,
    setDirtyRouteIds,
    setHasUnsavedSessionChanges,
    setFocusedRouteIds,
  ]);

  const { generate, createRoutesFromBuilder } = useRouteBuilderGenerate({
    companyId,
    inputs: effectiveInputs,
    properties: selectedProperties,
    session: sessionApi.session,
    activeDraftId,
    setSession: sessionApi.setSession,
    setInputs,
    setSelectedRouteId: sessionApi.setSelectedRouteId,
    setSelectedPropertyId: sessionApi.setSelectedPropertyVisitId,
    setDirtyRouteIds: sessionApi.setDirtyRouteIds,
    setHasUnsavedSessionChanges: sessionApi.setHasUnsavedSessionChanges,
    setFocusedRouteIds: sessionApi.setFocusedRouteIds,
    setMapHiddenRouteIds: sessionApi.setMapHiddenRouteIds,
    setIsBusy,
    setViewMode: sessionApi.setViewMode,
    refreshDrafts: draftsApi.refreshDrafts,
    resetBuilder,
    onRoutesCreated,
    onRoutesGenerated,
    guardOptimization,
    handleOptimizationError,
    refreshAccountUsage,
  });

  const setSetupLocation = useCallback(
    (type: RouteEndpointType, location: RouteEndpointLocation | null) => {
      const next = location ?? EMPTY_ROUTE_ENDPOINT;
      setInputs((prev) => {
        const resolved = resolveGenerationInputs(prev, locations);
        return {
          ...resolved,
          [`${type}Location`]: next,
          ...(type === "start" && resolved.sameStartAndEndLocation
            ? { endLocation: next }
            : {}),
        };
      });
    },
    [locations],
  );

  const setRouteEndpoint = useCallback(
    (
      routeId: string,
      type: RouteEndpointType,
      location: RouteEndpointLocation | null,
    ) => {
      setSessionRouteEndpoint(routeId, type, location ?? EMPTY_ROUTE_ENDPOINT);
    },
    [setSessionRouteEndpoint],
  );

  const saveCompanyLocation = useCallback(
    (values: CompanyLocationFormValues): Promise<CompanyLocation | null> => {
      const savedLocation: CompanyLocation = {
        name: values.name,
        location: values.location,
        address: values.address,
        _id: objectId.newObjectId(),
      };
      setIsSavingLocation(true);
      return new Promise((resolve) => {
        axiosCompanyLocations.addLocation(
          { companyId, ...savedLocation },
          () => {
            setLocations((prev) => [...prev, savedLocation]);
            setIsSavingLocation(false);
            flash.success("Location added");
            resolve(savedLocation);
          },
          () => {
            setIsSavingLocation(false);
            flash.error("Failed to save location");
            resolve(null);
          },
        );
      });
    },
    [companyId, setLocations],
  );

  const createBlankSession = useCallback(
    (season: Season) => {
      const nextInputs = {
        ...defaultInputs(locations),
        season,
        routeType: "recurring" as const,
        routeCount: 1,
        startLocation: { ...EMPTY_ROUTE_ENDPOINT },
        endLocation: { ...EMPTY_ROUTE_ENDPOINT },
      };
      const base: RouteBuilderSession = {
        id: createSessionId("blank"),
        companyId,
        status: "adjusted",
        origin: "blank",
        inputs: { ...nextInputs, routeCount: 0 },
        // Blank sessions use the catalog for their chosen season as visits are added.
        availableProperties: [],
        routes: [],
        unroutedJobs: [],
        createdAt: nextInputs.plannedDispatchTime,
      };
      const next = addRouteToSession(base);
      setInputs(nextInputs);
      setSession(next);
      setSelectedRouteId(next.routes[0]?.id ?? null);
      setSelectedPropertyVisitId(null);
      setDirtyRouteIds(new Set());
      setHasUnsavedSessionChanges(true);
      setFocusedRouteIds(null);
      setActiveDraftId(null);
      onRoutesGenerated?.();
    },
    [
      locations,
      companyId,
      setSession,
      setSelectedRouteId,
      setSelectedPropertyVisitId,
      setDirtyRouteIds,
      setHasUnsavedSessionChanges,
      setFocusedRouteIds,
      onRoutesGenerated,
    ],
  );

  const openEditSession = useCallback(
    async (routeIds: string[]) => {
      if (routeIds.length === 0) return false;
      setIsBusy(true);
      try {
        const next = await routeBuilder.loadRoutesForEdit(routeIds, companyId);
        setInputs(next.inputs);
        setSession(next);
        setSelectedRouteId(next.routes[0]?.id ?? null);
        setSelectedPropertyVisitId(null);
        setDirtyRouteIds(new Set());
        setHasUnsavedSessionChanges(false);
        setFocusedRouteIds(null);
        setActiveDraftId(null);
        onRoutesGenerated?.();
        return true;
      } catch (error) {
        console.error(error);
        void flash.error("Failed to open the selected routes for editing.");
        return false;
      } finally {
        setIsBusy(false);
      }
    },
    [
      companyId,
      onRoutesGenerated,
      setDirtyRouteIds,
      setFocusedRouteIds,
      setHasUnsavedSessionChanges,
      setSelectedPropertyVisitId,
      setSelectedRouteId,
      setSession,
    ],
  );

  const value: RouteBuilderContextValue = {
    inputs: effectiveInputs,
    setInputs,
    routeNameOverride,
    setRouteNameOverride,
    session: sessionApi.session,
    viewMode: sessionApi.viewMode,
    setViewMode: sessionApi.setViewMode,
    selectedRouteId: sessionApi.selectedRouteId,
    setSelectedRouteId: sessionApi.setSelectedRouteId,
    selectedPropertyVisitId: sessionApi.selectedPropertyVisitId,
    setSelectedPropertyVisitId: sessionApi.setSelectedPropertyVisitId,
    listRevealNonce: sessionApi.listRevealNonce,
    selectPropertyFromMap: sessionApi.selectPropertyFromMap,
    mapFocusNonce: sessionApi.mapFocusNonce,
    mapFocusPropertyVisitId: sessionApi.mapFocusPropertyVisitId,
    focusPropertyOnMap: sessionApi.focusPropertyOnMap,
    dirtyRouteIds: sessionApi.dirtyRouteIds,
    hasUnsavedSessionChanges: sessionApi.hasUnsavedSessionChanges,
    selectedPropertyCount: selectedProperties.length,
    allProperties,
    locations,
    availableServices,
    focusedRouteIds: sessionApi.focusedRouteIds,
    needsRouteFocusMode: sessionApi.needsRouteFocusMode,
    visibleRoutes: sessionApi.visibleRoutes,
    mapVisibleRoutes: sessionApi.mapVisibleRoutes,
    isRouteMapVisible: sessionApi.isRouteMapVisible,
    toggleRouteMapVisibility: sessionApi.toggleRouteMapVisibility,
    activeDraftId,
    isBusy,
    isSavingLocation,
    isLoadingResources,
    isRefreshingProperties,
    builderMode: sessionApi.session?.builderMode ?? "generate",
    optimizationBlocked,
    optimizationCreditsWarning: isLoadingPaywall
      ? ""
      : routeOptimizationCreditsWarning,
    showOptimizationNotice,
    guardOptimization,
    setFocusedRoutes: sessionApi.setFocusedRoutes,
    clearRouteFocus: sessionApi.clearRouteFocus,
    generate,
    createBlankSession,
    loadDraft: draftsApi.loadDraft,
    openEditSession,
    saveSessionAsDraft: draftsApi.saveSessionAsDraft,
    saveActiveDraftChanges: draftsApi.saveActiveDraftChanges,
    createRoutesFromBuilder,
    resetBuilder,
    reoptimizeRoute: sessionApi.reoptimizeRoute,
    reoptimizeAll: sessionApi.reoptimizeAll,
    movePropertyVisit: sessionApi.movePropertyVisit,
    addPropertyVisit: sessionApi.addPropertyVisit,
    addPropertiesToUnassigned: sessionApi.addPropertiesToUnassigned,
    addUnassignedFilters: sessionApi.addUnassignedFilters,
    setAddUnassignedFilters: sessionApi.setAddUnassignedFilters,
    assignPropertyVisitToRoute: sessionApi.assignPropertyVisitToRoute,
    removePropertyVisit: sessionApi.removePropertyVisit,
    unassignPropertyVisit: sessionApi.unassignPropertyVisit,
    confirmMoveToUnassigned: sessionApi.confirmMoveToUnassigned,
    resetMoveToUnassignedConfirm: sessionApi.resetMoveToUnassignedConfirm,
    registerOpenUnassignedPanel: sessionApi.registerOpenUnassignedPanel,
    addRoute: sessionApi.addRoute,
    removeRoute: sessionApi.removeRoute,
    reorderRoutes: sessionApi.reorderRoutes,
    renameRoute: sessionApi.renameRoute,
    setRouteColor: sessionApi.setRouteColor,
    setRouteInstructions: sessionApi.setRouteInstructions,
    updateProperty: sessionApi.updateProperty,
    setLock: sessionApi.setLock,
    setSetupLocation,
    setRouteEndpoint,
    saveCompanyLocation,
    getProperty: sessionApi.getProperty,
    getPropertyForVisit: sessionApi.getPropertyForVisit,
  };

  return (
    <RouteBuilderContext.Provider value={value}>
      {children}
    </RouteBuilderContext.Provider>
  );
}

export function RouteBuilderProvider(props: {
  children: React.ReactNode;
  onRoutesCreated?: () => void;
  onRoutesGenerated?: () => void;
}) {
  return (
    <PaywallProvider>
      <RouteBuilderProviderInner {...props} />
    </PaywallProvider>
  );
}

export function useRouteBuilder() {
  const ctx = useContext(RouteBuilderContext);
  if (!ctx) {
    throw new Error("useRouteBuilder must be used within RouteBuilderProvider");
  }
  return ctx;
}
